import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Wallet, Building2, Smartphone, Save, Loader2, CheckCircle, 
  AlertTriangle, ChevronLeft, Shield, Info
} from 'lucide-react';

const GHANA_BANKS = [
  { code: 'GCB', name: 'GCB Bank' },
  { code: 'ADB', name: 'Agricultural Development Bank' },
  { code: 'UBA', name: 'United Bank for Africa' },
  { code: 'STANBIC', name: 'Stanbic Bank Ghana' },
  { code: 'ABSA', name: 'Absa Bank Ghana' },
  { code: 'ECOBANK', name: 'Ecobank Ghana' },
  { code: 'ZENITH', name: 'Zenith Bank Ghana' },
  { code: 'FIDELITY', name: 'Fidelity Bank Ghana' },
  { code: 'CALBANK', name: 'CalBank' },
  { code: 'ACCESS', name: 'Access Bank Ghana' },
  { code: 'FBN', name: 'First Bank of Nigeria (Ghana)' },
  { code: 'GTB', name: 'Guaranty Trust Bank Ghana' },
  { code: 'REPUBLIC', name: 'Republic Bank Ghana' },
  { code: 'SOCIETE', name: 'Société Générale Ghana' },
  { code: 'PRUDENTIAL', name: 'Prudential Bank' },
];

const MOBILE_MONEY_PROVIDERS = [
  { code: 'mtn', name: 'MTN Mobile Money' },
  { code: 'vod', name: 'Vodafone Cash' },
  { code: 'tgo', name: 'AirtelTigo Money' },
];

export default function PayoutSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payoutType, setPayoutType] = useState('mobile_money');
  const [existingDetails, setExistingDetails] = useState(null);
  
  // Bank details
  const [bankCode, setBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  
  // Mobile Money details
  const [mobileMoneyProvider, setMobileMoneyProvider] = useState('mtn');
  const [mobileMoneyNumber, setMobileMoneyNumber] = useState('');

  useEffect(() => {
    const fetchPayoutDetails = async () => {
      try {
        const res = await API.get('/sellers/payout-details');
        if (res.data.has_payout_details) {
          setExistingDetails(res.data);
          setPayoutType(res.data.payout_type);
          if (res.data.payout_type === 'bank') {
            setBankCode(res.data.bank_code || '');
            setAccountName(res.data.account_name || '');
          } else {
            setMobileMoneyProvider(res.data.mobile_money_provider || 'mtn');
          }
        }
      } catch (error) {
        console.error('Error fetching payout details:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPayoutDetails();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (payoutType === 'bank') {
      if (!bankCode || !accountNumber || !accountName) {
        toast.error('Please fill in all bank details');
        return;
      }
      if (accountNumber.length < 10 || accountNumber.length > 16) {
        toast.error('Account number should be 10-16 digits');
        return;
      }
    } else {
      if (!mobileMoneyProvider || !mobileMoneyNumber) {
        toast.error('Please fill in all mobile money details');
        return;
      }
      if (!/^0[235]\d{8}$/.test(mobileMoneyNumber)) {
        toast.error('Please enter a valid Ghana mobile number (e.g., 0241234567)');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        payout_type: payoutType,
        ...(payoutType === 'bank' ? {
          bank_code: bankCode,
          account_number: accountNumber,
          account_name: accountName,
        } : {
          mobile_money_provider: mobileMoneyProvider,
          mobile_money_number: mobileMoneyNumber,
        })
      };

      await API.post('/sellers/payout-details', payload);
      toast.success('Payout details saved successfully!');
      
      // Refresh existing details
      const res = await API.get('/sellers/payout-details');
      setExistingDetails(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payout details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-background py-10 px-4">
        <div className="max-w-2xl mx-auto flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-gold" />
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background" data-testid="payout-settings-page">
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <Button variant="ghost" className="gap-2 mb-6" onClick={() => navigate(-1)}>
          <ChevronLeft className="w-4 h-4" /> Back
        </Button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
            <Wallet className="w-6 h-6 text-gold" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Payout Settings</h1>
            <p className="text-sm text-muted-foreground">Set up how you want to receive your payments</p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-400 mb-1">Automatic Payouts</p>
            <p className="text-muted-foreground">
              When a buyer confirms delivery, your payment (minus the 1.5% platform fee) will be automatically sent to your configured account within 24 hours.
            </p>
          </div>
        </div>

        {/* Existing Details Status */}
        {existingDetails?.has_payout_details && (
          <div className="mt-6 bg-trust-green/10 border border-trust-green/30 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-trust-green flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-trust-green mb-1">Payout Method Configured</p>
              <p className="text-muted-foreground">
                {existingDetails.payout_type === 'bank' 
                  ? `Bank Account: ${existingDetails.account_name} (****${existingDetails.account_number?.slice(-4)})`
                  : `Mobile Money: ${MOBILE_MONEY_PROVIDERS.find(p => p.code === existingDetails.mobile_money_provider)?.name || existingDetails.mobile_money_provider} (${existingDetails.mobile_money_number})`
                }
              </p>
            </div>
          </div>
        )}

        {/* Payout Form */}
        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          {/* Payout Type Selection */}
          <div className="bg-card border border-border/50 rounded-xl p-6">
            <h2 className="font-semibold mb-4">Choose Payout Method</h2>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setPayoutType('mobile_money')}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  payoutType === 'mobile_money' 
                    ? 'border-gold bg-gold/10' 
                    : 'border-border/50 hover:border-gold/50'
                }`}
                data-testid="select-mobile-money"
              >
                <Smartphone className={`w-6 h-6 mb-2 ${payoutType === 'mobile_money' ? 'text-gold' : 'text-muted-foreground'}`} />
                <p className="font-medium">Mobile Money</p>
                <p className="text-xs text-muted-foreground mt-1">MTN, Vodafone, AirtelTigo</p>
              </button>
              
              <button
                type="button"
                onClick={() => setPayoutType('bank')}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  payoutType === 'bank' 
                    ? 'border-gold bg-gold/10' 
                    : 'border-border/50 hover:border-gold/50'
                }`}
                data-testid="select-bank"
              >
                <Building2 className={`w-6 h-6 mb-2 ${payoutType === 'bank' ? 'text-gold' : 'text-muted-foreground'}`} />
                <p className="font-medium">Bank Account</p>
                <p className="text-xs text-muted-foreground mt-1">Ghana bank transfer</p>
              </button>
            </div>
          </div>

          {/* Mobile Money Details */}
          {payoutType === 'mobile_money' && (
            <div className="bg-card border border-border/50 rounded-xl p-6 space-y-4">
              <h2 className="font-semibold flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-gold" />
                Mobile Money Details
              </h2>
              
              <div className="space-y-2">
                <Label>Provider</Label>
                <Select value={mobileMoneyProvider} onValueChange={setMobileMoneyProvider}>
                  <SelectTrigger className="h-12" data-testid="momo-provider-select">
                    <SelectValue placeholder="Select provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {MOBILE_MONEY_PROVIDERS.map(p => (
                      <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Mobile Number</Label>
                <Input
                  type="tel"
                  placeholder="e.g., 0241234567"
                  value={mobileMoneyNumber}
                  onChange={(e) => setMobileMoneyNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className="h-12 font-mono-display"
                  maxLength={10}
                  data-testid="momo-number-input"
                />
                <p className="text-xs text-muted-foreground">Enter the number registered with your mobile money account</p>
              </div>
            </div>
          )}

          {/* Bank Details */}
          {payoutType === 'bank' && (
            <div className="bg-card border border-border/50 rounded-xl p-6 space-y-4">
              <h2 className="font-semibold flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gold" />
                Bank Account Details
              </h2>
              
              <div className="space-y-2">
                <Label>Bank</Label>
                <Select value={bankCode} onValueChange={setBankCode}>
                  <SelectTrigger className="h-12" data-testid="bank-select">
                    <SelectValue placeholder="Select your bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {GHANA_BANKS.map(b => (
                      <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Account Number</Label>
                <Input
                  type="text"
                  placeholder="Enter your account number"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                  className="h-12 font-mono-display"
                  maxLength={16}
                  data-testid="account-number-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Account Name</Label>
                <Input
                  type="text"
                  placeholder="Name as it appears on your account"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  className="h-12"
                  data-testid="account-name-input"
                />
              </div>
            </div>
          )}

          {/* Security Notice */}
          <div className="bg-card border border-border/50 rounded-xl p-4 flex items-start gap-3">
            <Shield className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium mb-1">Secure & Encrypted</p>
              <p className="text-muted-foreground">
                Your payout information is encrypted and securely stored. We use Paystack's secure infrastructure for all payouts.
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={saving}
            className="w-full h-12 bg-gold text-black hover:brightness-110 font-semibold gap-2"
            data-testid="save-payout-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Payout Details
              </>
            )}
          </Button>
        </form>
      </div>
    </main>
  );
}
